import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

import AdminPortal from 'entity/AdminPortal';
import Company from 'entity/Company';
import CompanyEmailDomain from 'entity/CompanyEmailDomain';
import CompanySetting from 'entity/CompanySetting';
import Department from 'entity/Department';
import InviteInfo from 'entity/InviteInfo';
import Location from 'entity/Location';
import Member from 'entity/Member';
import Team from 'entity/Team';
import User from 'entity/User';
import LicenseType from 'enums/LicenseType';
import MemberType from 'enums/MemberType';
import AdminPortalController from 'rest/AdminPortalController';
import CompanyController from 'rest/CompanyController';
import CompanyEmailDomainController from 'rest/CompanyEmailDomainController';
import Paging from 'rest/Paging';
import SettingController from 'rest/SettingController';

@Injectable()
export class AdminService {
  private readonly _adminPortal = new BehaviorSubject<AdminPortal>({});
  public readonly adminPortal$ = this._adminPortal.asObservable();
  private adminPortalController: AdminPortalController;

  public get adminPortal(): AdminPortal {
    return this._adminPortal.getValue();
  }

  public set adminPortal(portal: AdminPortal) {
    this._adminPortal.next(portal);
  }

  public convertCompany(company: Company): Observable<Company> {
    return from(new CompanyController().convertMyCompany(company));
  }

  public constructor() {
    this.adminPortalController = new AdminPortalController();
  }

  public sendCompanyInvite(unsentOnly: boolean): Observable<InviteInfo[]> {
    return from(new AdminPortalController().sendCompanyInviteEmails(unsentOnly));
  }

  public sendTeamInviteEmails(unsentOnly: boolean): Observable<InviteInfo[]> {
    return from(new AdminPortalController().sendTeamInviteEmails(unsentOnly));
  }

  public getCompanyUsers(companyId: string): Observable<AdminPortal> {
    return from(new AdminPortalController().selectCompany(companyId));
  }

  public setTeamForUser(teamId: string, userId: string): Observable<Member> {
    return from(new AdminPortalController().setTeamForUser(teamId, userId));
  }

  public setDepartmentForUser(departmentId: string, userId: string): Observable<Department> {
    return from(new AdminPortalController().setDepartmentForUser(departmentId, userId));
  }

  public setLocationForUser(locationId: string, userId: string): Observable<Location> {
    return from(new AdminPortalController().setLocationForUser(locationId, userId));
  }

  public createLocation(location: Location): Observable<Location> {
    return from(new AdminPortalController().createLocation(location));
  }

  public updateLocation(location: Location): Observable<Location> {
    return from(new AdminPortalController().updateLocation(location));
  }

  public createDepartment(department: Department): Observable<Department> {
    return from(new AdminPortalController().createDepartment(department));
  }

  public updateDepartment(department: Department): Observable<Department> {
    return from(new AdminPortalController().updateDepartment(department));
  }

  public createTeam(team: Team): Observable<Team> {
    return from(new AdminPortalController().createTeam(team));
  }

  public updateTeam(team: Team): Observable<Team> {
    return from(new AdminPortalController().updateTeam(team));
  }

  public getSCIMKey(): Observable<string> {
    return from(new AdminPortalController().getScimKey());
  }

  public generateSCIMKey(): Observable<string> {
    return from(new AdminPortalController().createScimKey());
  }

  public satDescription(text: string): Observable<void> {
    return from(new AdminPortalController().setDescription(text));
  }

  public getDomains(): Observable<string[]> {
    return from(new AdminPortalController().getEmailDomains());
  }

  public getCompany(): Observable<Company> {
    return from(new CompanyController().getCurrentCompany());
  }

  public updateCompany(company: Company, companyId: string): Observable<Company> {
    return from(new CompanyController().updateCompany(company, companyId));
  }

  public getCompanyDomains(companyId: string): Observable<CompanyEmailDomain[]> {
    return from(new CompanyEmailDomainController().getCompanyDomains(companyId));
  }

  public getAdminPortal(): Observable<AdminPortal> {
    return from(new AdminPortalController().getAdminPortal());
  }

  public selectTeam(teamId: string): Observable<AdminPortal> {
    return from(new AdminPortalController().selectTeam(teamId));
  }

  public selectCompany(companyId: string): Observable<AdminPortal> {
    return from(new AdminPortalController().selectCompany(companyId));
  }

  public getTeams(page?: Paging): Observable<Team[]> {
    return from(new AdminPortalController().getTeams(page));
  }

  public joinTeam(teamId: string): Observable<string> {
    return from(new AdminPortalController().joinTeam(teamId));
  }

  public findMember(joined: boolean, text?: string, page?: Paging): Observable<Member[]> {
    return from(new AdminPortalController().findMembers(joined, text, page));
  }

  public removeMember(userId: string): Observable<void> {
    return from(new AdminPortalController().removeMember(userId));
  }

  public blockUser(userId: string, companyId: string): Observable<void> {
    return from(new CompanyController().blockUser(companyId, userId));
  }

  public revokeInviteForUser(member: Member): Observable<string> {
    return from(new AdminPortalController().revokeInviteForUser(member));
  }

  public updateMember(member: Member): Observable<Member> {
    return from(new AdminPortalController().updateMember(member));
  }

  public inviteMember(member: User): Observable<string> {
    return from(new AdminPortalController().inviteMember(member));
  }

  public inviteMembers(role: MemberType, license: LicenseType, emails: string[]): Promise<void> {
    return this.adminPortalController.inviteMembers(role, license, emails);
  }

  public inviteExistingMember(member: Member): Observable<string> {
    return from(new AdminPortalController().inviteExistingMember(member));
  }

  public setMemberRole(userId: string, role: MemberType): Observable<void> {
    return from(new AdminPortalController().setMemberRole(userId, role));
  }

  public getCompanySettings(companyId: string): Observable<CompanySetting[]> {
    return from(new SettingController().getCompanySettings(companyId));
  }

  public saveCompanySettings(companySetting: CompanySetting): Observable<CompanySetting> {
    return from(new SettingController().saveCompanySetting(companySetting));
  }

  public addDomain(companyDomain: CompanyEmailDomain): Observable<CompanyEmailDomain> {
    return from(new CompanyEmailDomainController().addCompanyDomain(companyDomain));
  }

  public removeDomain(companyEmailDomainId: number): Observable<void> {
    return from(new CompanyEmailDomainController().removeCompanyDomain(companyEmailDomainId));
  }

  public changeDomainStatus(companyEmailDomainId: number, status: number): Observable<CompanyEmailDomain> {
    return from(new CompanyEmailDomainController().changeDomainStatus(companyEmailDomainId, status));
  }

  /**
   * Verify the company domain using the admin email.
   *
   * If the admin has a matching verified email domain, this returns true and the domain is now verified.
   * @param emailDomain Email domain to verified
   * @returns True if the domain is verified
   */
  public setEmailDomainVerified(emailDomain: string): Observable<boolean> {
    return from(new CompanyController().setEmailDomainVerified(emailDomain));
  }

  public isAdmin(): Observable<boolean> {
    return from(new AdminPortalController().isAdmin());
  }

  public isAdminDirect(): Promise<boolean> {
    return new AdminPortalController().isAdmin();
  }

  public getUsersFromCompany(page?: Paging): Observable<User[]> {
    return from(new CompanyController().getMyCompanyUsers(page));
  }
}
